import { useEffect, useState, type RefObject } from 'react';

interface UseInViewOptions {
  /** Fraction of the element that must be visible. */
  threshold?: number;
  rootMargin?: string;
  /** Stop observing after the first entry (default true). */
  once?: boolean;
}

/** Reports whether `ref` has entered the viewport (IntersectionObserver; true immediately when unsupported). */
export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { threshold = 0.2, rootMargin = '0px 0px -8% 0px', once = true }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Already above the viewport (mounted after the visitor scrolled past): count it as seen.
        const passed = once && entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
        if (entry.isIntersecting || passed) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin, once]);

  return inView;
}
